/**
 *
 * Application.react.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 * NOTE: while this component should technically be a stateless functional
 * component (SFC), hot reloading does not currently support SFCs. If hot
 * reloading is not a necessity for you then you can refactor it and remove
 * the linting exception.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectSaga from 'utils/injectSaga';
import { Helmet } from 'react-helmet';
import { Switch, Route, withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { injectIntl } from 'react-intl';
import styled, { ThemeProvider } from 'styled-components';

import Options from 'domain/Options';

import HomePage from 'pages/HomePage/Loadable';
import MainPage from 'pages/MainPage/Loadable';
import NotFoundPage from 'pages/NotFoundPage/Loadable';
import ItemListPage from 'pages/ItemListPage/Loadable';
import ComparePage from 'pages/ComparePage/Loadable';

import LoadPlanErrorDialog from 'ui/Dialog/LoadPlanErrorDialog';
import version from 'version.json';
import { savePlan } from 'domain/Options/actions';
import LoadLocalPlanErrorDialog from 'ui/Dialog/LoadLocalPlanErrorDialog';
import SavePlanErrorDialog from 'ui/Dialog/SavePlanErrorDialog';
import ScreenSaver from 'ui/ScreenSaver/Loadable';
import IdleTimeChecker from 'ui/ScreenSaver/IdleTimeChecker';
import localStorageSaga from 'domain/LocalStorage/sagas';
import { setLastVersion as setLastVersionAction } from 'domain/LocalStorage/actions';
import { height } from 'ui/HeaderContainer';
import { media } from 'style-utils';
import messages from './messages';
import { loadApplication, setLoadPlanFromLinkError, setLoadLocalPlanError, setSaveError } from './actions';
import {
  loadPlanFromLinkErrorSelector,
  loadLocalPlanErrorSelector,
  saveErrorSelector,
  applicationModeSelector,
  planLoadingSelector,
  redirectAfterSaveSelector,
  showNewFeaturesToastSelector,
  ipexGalleryModeSelector,
  idleTimeSelector,
  kioskPrintModeSelector,
  upptackaModeSelector,
} from './selectors';
import { cwtTranlationSelector,  experienceGalleryBackURLSelector, upptackaIntegrationSelector } from 'domain/LanguageProvider/selectors';
import { debugSelector, debugShape } from './debug';
import { KIOSK } from './constants';
import saga from './sagas';

import ScrollToTop from './ScrollToTop';
import CountdownBanner from 'ui/CountDownBanner';


const applicationContextTypes = {
  debug: debugShape,
  applicationMode: PropTypes.string,
};

const applicationContextShape = PropTypes.shape(applicationContextTypes);

export { applicationContextTypes, applicationContextShape };

const paddingTopExpr = (headerHeight) => ({ newFeaturesToastVisible }) => {
  const toastHeight = 32;
  return `
    padding-top: ${newFeaturesToastVisible ? `${toastHeight + headerHeight}px` : `${headerHeight}px`};
  `;
};
const ApplicationWrapper = styled.div`
  @media screen { /* no padding for print */
    ${paddingTopExpr(height.mobile)}
    ${media.desktop`
      ${paddingTopExpr(height.desktop)}
    `}
  }
`;
class Application extends React.Component { // eslint-disable-line react/prefer-stateless-function

  static propTypes = {
    intl: PropTypes.object.isRequired,
    debug: PropTypes.object.isRequired,
    loadApplication: PropTypes.func.isRequired,
    loadPlanErrorDismissed: PropTypes.func.isRequired,
    loadPlanFromLinkError: PropTypes.bool.isRequired,
    loadLocalPlanError: PropTypes.bool.isRequired,
    loadLocalPlanErrorDismissed: PropTypes.func.isRequired,
    saveError: PropTypes.bool.isRequired,
    planLoading: PropTypes.bool.isRequired,
    redirectAfterSave: PropTypes.bool.isRequired,
    saveErrorDismissed: PropTypes.func.isRequired,
    retrySavePlan: PropTypes.func.isRequired,
    applicationMode: PropTypes.string.isRequired,
    setLastVersion: PropTypes.func.isRequired,
    showNewFeaturesToast: PropTypes.bool.isRequired,
  };

  static childContextTypes = applicationContextTypes

  getChildContext = () => {
    return {
      debug: this.props.debug,
      applicationMode: this.props.applicationMode,
    };
  }

  UNSAFE_componentWillMount = () => {
    this.props.loadApplication();
    this.props.setLastVersion(version.buildVersion);
  }

  onRetrySave = () => {
    const {
      retrySavePlan,
      redirectAfterSave,
    } = this.props;

    retrySavePlan(redirectAfterSave);
  }

  getTheme = () => {
    return {
      ...this.getChildContext(),
    };
  }

  render() {
    const {
      loadPlanFromLinkError,
      loadLocalPlanError,
      loadPlanErrorDismissed,
      loadLocalPlanErrorDismissed,
      saveError,
      saveErrorDismissed,
      applicationMode,
      planLoading,
      showNewFeaturesToast,
      cwtTranslation,
      cwcalcReachedFromIpexGallery,
      cwcalcReachedFromUpptacka,
      upptackaIntegration,
      idleTime,
      experienceGalleryBackURL,
      printOptionOnKiosk,
    } = this.props;

    return (
      <ThemeProvider theme={this.getTheme()}>
        <ApplicationWrapper newFeaturesToastVisible={showNewFeaturesToast}>
          <Helmet>
            <title>{this.props.intl.formatMessage(messages.websiteTitle)}</title>
          </Helmet>
          <LoadPlanErrorDialog show={loadPlanFromLinkError} onDismiss={loadPlanErrorDismissed} cwcalcReachedFromIpexGallery={cwcalcReachedFromIpexGallery}/>
          <LoadLocalPlanErrorDialog show={loadLocalPlanError} onDismiss={loadLocalPlanErrorDismissed} cwcalcReachedFromIpexGallery={cwcalcReachedFromIpexGallery}/>
          <SavePlanErrorDialog loading={planLoading} show={saveError} onDismiss={saveErrorDismissed} onSave={this.onRetrySave} cwcalcReachedFromIpexGallery={cwcalcReachedFromIpexGallery}/>
          <Options>
            <ScrollToTop cwcalcReachedFromUpptacka={cwcalcReachedFromUpptacka}>
              <Switch>
                <Route exact path="/" component={HomePage} />
                <Route path="/main" component={MainPage} />
                <Route path="/itemlist" component={ItemListPage} />
                <Route path="/compare" component={ComparePage} />
                <Route component={NotFoundPage} />
              </Switch>
            </ScrollToTop>
          </Options>
          { applicationMode === KIOSK && cwcalcReachedFromUpptacka && <IdleTimeChecker upptackaIntegration={upptackaIntegration}/>}
          { applicationMode === KIOSK && !cwcalcReachedFromUpptacka && !cwcalcReachedFromIpexGallery && <ScreenSaver /> }
          { applicationMode === KIOSK && cwcalcReachedFromIpexGallery && <CountdownBanner idleTime={idleTime} cwtTranslation={cwtTranslation} experienceGalleryBackURL={experienceGalleryBackURL}/>} 
        </ApplicationWrapper>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  loadPlanFromLinkError: loadPlanFromLinkErrorSelector,
  loadLocalPlanError: loadLocalPlanErrorSelector,
  saveError: saveErrorSelector,
  debug: debugSelector,
  applicationMode: applicationModeSelector,
  planLoading: planLoadingSelector,
  redirectAfterSave: redirectAfterSaveSelector,
  showNewFeaturesToast: showNewFeaturesToastSelector,
  cwtTranslation: cwtTranlationSelector,
  cwcalcReachedFromIpexGallery: ipexGalleryModeSelector,
  cwcalcReachedFromUpptacka: upptackaModeSelector,
  idleTime: idleTimeSelector,
  experienceGalleryBackURL:  experienceGalleryBackURLSelector,
  printOptionOnKiosk: kioskPrintModeSelector,
  upptackaIntegration: upptackaIntegrationSelector,
});

function mapDispatchToProps(dispatch) {
  return {
    loadApplication: () => {
      dispatch(loadApplication());
    },
    loadPlanErrorDismissed: () => {
      dispatch(setLoadPlanFromLinkError(false));
    },
    loadLocalPlanErrorDismissed: () => {
      dispatch(setLoadLocalPlanError(false));
    },
    saveErrorDismissed: () => {
      dispatch(setSaveError(false));
    },
    retrySavePlan: (redirect) => {
      dispatch(savePlan(redirect));
    },
    setLastVersion: (lastVersion) => {
      dispatch(setLastVersionAction(lastVersion));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withSaga = injectSaga({ key: 'application', saga });
const withLocalStorageSaga = injectSaga({ key: 'localStorage', saga: localStorageSaga });

export default compose(
  withSaga,
  withLocalStorageSaga,
  withRouter,
  withConnect,
  injectIntl
)(Application);
